import {
  Component,
  ElementRef,
  Input,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NgpMenuItem } from 'ng-primitives/menu';

/**
 * `<button sui-menu-item>` / `<a sui-menu-item>` — an interactive row inside a
 * `<sui-menu>`. Hosts `NgpMenuItem` (`role=menuitem`, roving focus,
 * click-closes-the-menu). Content is neutral `<ng-content>` — call sites bring
 * their own icon + label markup, so the varied existing rows (indicators,
 * coloured sentiment dots, …) migrate without fighting an opinionated slot.
 *
 * **Two host elements:** a `<button>` for action items, or an `<a>` for
 * navigation items (carry `[routerLink]`/`href` — preserves link semantics:
 * cmd/middle-click → new tab, hover preview, a11y). `NgpMenuItem` works on
 * either.
 *
 * `[disabled]` is aliased onto the primitive's own disabled (roving focus then
 * skips the item) AND — **for the `<button>` variant only** — mirrored to the
 * native `disabled` attribute, which blocks click + focus so the menu can't be
 * closed by activating a disabled row (CRAFTING-GUIDE §8). Anchors have no
 * native `disabled`; an `<a sui-menu-item [disabled]>` is skipped by keyboard
 * roving but not click-blocked — don't rely on disabling link items.
 *
 * **`[selected]`** turns the item into a single-select "value-pick" row: set it
 * (`true`/`false`) and the item becomes `role=menuitemradio` with `aria-checked`,
 * and a trailing ✓ slot is reserved (revealed when `true`). Leave it unset for a
 * plain action item (`role=menuitem`, no ✓). The consumer still owns the click —
 * `[selected]` is display + a11y only.
 */
@Component({
  selector: 'button[sui-menu-item], a[sui-menu-item]',
  standalone: true,
  template:
    '<ng-content />' +
    '@if (selectable) { <span class="sui-menu-item__check" [class.sui-menu-item__check--on]="selected" aria-hidden="true"></span> }',
  styleUrl: './sui-menu-item.component.scss',
  hostDirectives: [
    { directive: NgpMenuItem, inputs: ['ngpMenuItemDisabled: disabled'] },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[attr.type]': 'isButton ? "button" : null',
    '[attr.disabled]': 'isButton && menuItem.disabled() ? "" : null',
    '[attr.role]': 'selectable ? "menuitemradio" : "menuitem"',
    '[attr.aria-checked]': 'selectable ? (selected ? "true" : "false") : null',
  },
})
export class SuiMenuItemComponent {
  protected readonly menuItem = inject(NgpMenuItem);
  private readonly host = inject(ElementRef<HTMLElement>);

  /**
   * Single-select indicator. Unset ⇒ plain action item (`role=menuitem`, no ✓).
   * Set ⇒ `role=menuitemradio` + `aria-checked`, with a trailing ✓ shown when true.
   */
  @Input() selected?: boolean;

  /** True when the host is a `<button>` (vs an `<a>`) — gates button-only host attrs. */
  protected get isButton(): boolean {
    return this.host.nativeElement.tagName === 'BUTTON';
  }

  /** Whether this item participates in single-select (i.e. `[selected]` was bound). */
  protected get selectable(): boolean {
    return this.selected !== undefined;
  }
}
